import { ComboBox } from "@/components/combox";
import { Separator } from "@/components/Separator";
import { Typography } from "@/components/typography";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/hooks/useAuth";
import { useTagService } from "@/services/useTagService";
import { useProjectService } from "@/services/useProjectService";
import { IProject } from "@/interfaces/IProject";
import { ITag } from "@/interfaces/ITag";
import { Trash } from "lucide-react";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";

const TasksEdit = () => {
  const { user } = useAuth();
  const params = useParams();
  const { getAll } = useTagService();
  const { getById, update, remove } = useProjectService();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [project, setProject] = useState<IProject | null>(null);
  const [tags, setTags] = useState<ITag[]>([]);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const fetchTags = async () => {
    const userId = user?.id;
    try {
      if (userId) {
        const query = `perPage=100&currentPage=1`;
        const response = await getAll(query);
        setTags(response?.data ?? []);
      }
    } catch (err) {
      console.error("Error to search tags:", err);
      setTags([]);
    }
  };

  const fetchData = async () => {
    const { id } = params;
    if (!id) {
      toast.error("ID task not found.");
      setLoading(false);
      return;
    }

    try {
      const dataProject: IProject | null = await getById(id);
      if (dataProject) {
        setProject(dataProject);
      } else {
        toast.error("Project not found.");
      }
    } catch (error: any) {
      toast.error("Error to search projects: " + error.message);
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    fetchTags();
  }, []);

  const handleUpdate = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setUpdating(true);
    try {
      const id = project?.id;
      if (id) {
        await update(id, project);
        toast.success("Project updated successfully!");
        navigate("/home");
      }
    } catch (error: any) {
      toast.error("Error to update project");
      console.error(error);
    } finally {
      setUpdating(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    try {
      const id = project?.id;
      if (id) {
        await remove(id);
        toast.success("Project deleted successfully!");
        navigate("/home");
      }
    } catch (error: any) {
      toast.error("Error to delete project");
      console.error(error);
    } finally {
      setDeleting(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setProject((prev) => (prev ? { ...prev, [name]: value } : prev));
  };

  const handleTagChange = (value: string | undefined) => {
    setProject((prev) => ({
      ...prev!,
      tagId: value,
    }));
  };

  return (
    <div className="p-10">
      <div className="flex flex-row items-center justify-between">
        <Typography as="h1">Update Env Project</Typography>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button type="button" variant="destructive">
              <Trash />
              Remove
            </Button>
          </DialogTrigger>
          <DialogContent className="sm:max-w-[600px]">
            <DialogHeader className="m-5">
              <DialogTitle>Do you want to remove this project?</DialogTitle>
              <DialogDescription className="flex flex-row justify-between mt-3">
                <Button
                  type="button"
                  variant="destructive"
                  size="lg"
                  loading={deleting}
                  onClick={handleDelete}
                >
                  <Trash />
                  Yes
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => setOpen(false)}
                >
                  No
                </Button>
              </DialogDescription>
            </DialogHeader>
          </DialogContent>
        </Dialog>
      </div>
      <Separator className="my-5" />

      {loading ? (
        <div className="space-y-4">
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-full" />
          <Skeleton className="h-9 w-[200px]" />
          <Skeleton className="h-48 w-full" />
        </div>
      ) : project ? (
        <form className="space-y-4" onSubmit={handleUpdate}>
          <div>
            <Typography as="p">Project Name</Typography>
            <Input
              type="text"
              name="title"
              placeholder="My awesome project"
              value={project.title}
              onChange={handleChange}
            />
          </div>

          <div>
            <Typography as="p">Description</Typography>
            <Input
              type="text"
              name="description"
              placeholder="Short description of the project"
              value={project.description}
              onChange={handleChange}
            />
          </div>

          <div className="w-min">
            <Typography as="p">Markers</Typography>
            <ComboBox
              options={tags}
              value={project.tagId ?? null}
              onChange={handleTagChange}
              placeholder="Select a tag"
            />
          </div>

          <div>
            <Typography as="p">Environment Variables</Typography>
            <Textarea
              name="variablesEnvironment"
              placeholder="VAR1=value1\nVAR2=value2"
              className="h-48"
              value={project.variablesEnvironment}
              onChange={handleChange}
            />
          </div>

          <div className="mt-5 flex justify-between">
            <Button type="submit" variant="default" loading={updating}>
              Update
            </Button>
            <Button type="button" onClick={() => navigate(-1)}>
              Exit
            </Button>
          </div>
        </form>
      ) : (
        <Typography as="p">Project not found.</Typography>
      )}
    </div>
  );
};

export default TasksEdit;
